import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { SlideStage } from '../../components/deck/SlideStage';
import { distributedProducts, operatorAssets, regionalStages, whyTelecom } from '../../data/deckSlide14';
const VEXU_LOGO = 'https://cdn.magicpatterns.com/uploads/3A8d459aXLumR8KKFCQtrD/vexu_logo.svg';
const ease = [0.22, 0.61, 0.36, 1] as const;
type Stage = 0 | 1 | 2 | 3 | 4 | 5;

/** Right-hand ecosystem geometry on the fixed 1600×900 canvas. */
const AXIS = 1090;
const PRODUCT_X = [744, 1140];
const PRODUCT_W = 300;
export function TelecomDistributionSlide() {
  const [stage, setStage] = useState<Stage>(0);
  useEffect(() => {
    const timers: number[] = [];
    const at = (ms: number, next: Stage) => timers.push(window.setTimeout(() => setStage(next), ms));
    at(700, 1); // operator
    at(1600, 2); // engine
    at(2500, 3); // the two products
    at(3600, 4); // operator distribution + why telecom
    at(5200, 5); // regional stages + takeaway
    return () => timers.forEach(id => window.clearTimeout(id));
  }, []);
  const shown = (s: Stage) => stage >= s;
  const connector = (top: number, height: number, at: Stage) => <motion.div className="absolute w-px" style={{
    left: AXIS,
    top,
    backgroundColor: 'rgba(0,200,83,0.4)'
  }} initial={{
    height: 0
  }} animate={{
    height: shown(at) ? height : 0
  }} transition={{
    duration: 0.6,
    ease
  }} data-id="element-2724" data-mp="2c5lrk6zsglag" />;
  return <SlideStage data-id="element-2725" data-mp="34rws5hneam79">
      <div className="deck-type absolute inset-0 overflow-hidden bg-[#FAFCF9]" data-id="element-2726" data-mp="38vmdzzcoqpol">
        <div className="absolute inset-0" style={{
        background: 'radial-gradient(ellipse 70% 60% at 72% -18%, rgba(0,200,83,0.07) 0%, rgba(250,252,249,0) 60%), linear-gradient(180deg, #FBFDFA 0%, #F5F8F3 58%, #EFF3ED 100%)'
      }} data-id="element-2727" data-mp="38tibnlufpyqf" />

        {/* Brand header */}
        <motion.div className="absolute flex items-center gap-3" style={{
        left: 112,
        top: 56
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.8,
        ease
      }} data-id="element-2728" data-mp="1ofrhtyarncuf">
          <img src={VEXU_LOGO} alt="" className="w-7 h-7 object-contain" data-id="element-2729" data-mp="10dcp63ym67d8" />
          <span className="text-[16px] font-bold tracking-tight text-[#0B1712]" data-id="element-2730" data-mp="vkt9n1gd925s">
            Vexu.ai
          </span>
        </motion.div>
        <motion.span className="absolute text-[11px] font-semibold uppercase tracking-[0.28em] text-[#8A998F]" style={{
        right: 112,
        top: 62
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.8,
        ease
      }} data-id="element-2731" data-mp="qlqfwd5u6id4">
          15 · Telecom Distribution
        </motion.span>

        {/* Headline */}
        <h1 className="absolute text-[40px] font-semibold leading-[1.16] tracking-[-0.02em] text-[#0B1712]" style={{
        left: 112,
        top: 126,
        width: 620
      }} data-id="element-2732" data-mp="7ofk52jey72f">
          {['From direct acquisition.', 'To telecom-scale distribution.'].map((line, i) => <motion.span key={line} className="block" initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.9,
          delay: 0.15 + i * 0.15,
          ease
        }} data-id="element-2733" data-mp="29lpp97s6acnc" data-mpi={i}>
                {line}
              </motion.span>)}
        </h1>
        <motion.p className="absolute text-[16px] leading-relaxed text-[#5A6B60]" style={{
        left: 112,
        top: 244,
        width: 500
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.9,
        delay: 0.7,
        ease
      }} data-id="element-2734" data-mp="2obnbvjageuaf">
          VEXU can extend beyond direct-to-consumer acquisition through telecom
          partnerships — creating a new distribution path for AI communication
          services.
        </motion.p>

        {/* LEFT — why telecom */}
        <div className="absolute" style={{
        left: 112,
        top: 370,
        width: 470
      }} data-id="element-2735" data-mp="3g0qgj06966nl">
          {whyTelecom.map((item, i) => <motion.div key={item.title} className="mb-6 border-t border-[#0B1712]/[0.09] pt-4 last:mb-0" initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: shown(4) ? 1 : 0,
          y: shown(4) ? 0 : 8
        }} transition={{
          duration: 0.7,
          delay: i * 0.18,
          ease
        }} data-id="element-2736" data-mp="3ekw3bfgdv7te" data-mpi={i}>
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-vexu-700" data-id="element-2737" data-mp="3jl0lrb0uqcm9">
                {item.title}
              </p>
              <p className="mt-2 text-[14px] leading-snug text-[#33463B]" data-id="element-2738" data-mp="3sk5jwg8i43xw">
                {item.detail}
              </p>
            </motion.div>)}
        </div>

        {/* RIGHT — the distribution ecosystem */}
        <motion.div className="absolute text-center" style={{
        left: AXIS - 200,
        top: 272,
        width: 400
      }} initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: shown(1) ? 1 : 0,
        y: shown(1) ? 0 : 8
      }} transition={{
        duration: 0.8,
        ease
      }} data-id="element-2739" data-mp="65cd6pb0e5ik">
          <p className="text-[13px] font-semibold uppercase tracking-[0.2em] text-[#0B1712]" data-id="element-2740" data-mp="yrkjngmc5nzo">
            Telecom operator
          </p>
        </motion.div>
        {connector(300, 42, 2)}

        <motion.div className="absolute text-center" style={{
        left: AXIS - 200,
        top: 350,
        width: 400
      }} initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: shown(2) ? 1 : 0,
        y: shown(2) ? 0 : 8
      }} transition={{
        duration: 0.8,
        ease
      }} data-id="element-2741" data-mp="pqmbcm4v6uba">
          <div className="inline-flex items-center gap-2.5" data-id="element-2742" data-mp="kqzyidsy7g30">
            <img src={VEXU_LOGO} alt="" className="h-5 w-5 object-contain" data-id="element-2743" data-mp="kskw6cak7f53" />
            <span className="text-[14px] font-semibold uppercase tracking-[0.2em] text-[#0B1712]" data-id="element-2744" data-mp="716b7zx3d3ve">
              VEXU AI Engine
            </span>
          </div>
        </motion.div>
        {connector(384, 30, 3)}

        {/* split into the two products */}
        <motion.div className="absolute h-px" style={{
        left: PRODUCT_X[0] + PRODUCT_W / 2,
        top: 414,
        width: PRODUCT_X[1] + PRODUCT_W / 2 - (PRODUCT_X[0] + PRODUCT_W / 2),
        backgroundColor: 'rgba(0,200,83,0.4)'
      }} initial={{
        scaleX: 0
      }} animate={{
        scaleX: shown(3) ? 1 : 0
      }} transition={{
        duration: 0.8,
        ease
      }} data-id="element-2745" data-mp="20i8wi3ex0lv" />
        {distributedProducts.map((product, i) => <div key={product.name} data-id="element-2746" data-mp="2mo0ib8rly2ok" data-mpi={i}>
            <motion.div className="absolute w-px" style={{
          left: PRODUCT_X[i] + PRODUCT_W / 2,
          top: 414,
          height: 26,
          backgroundColor: 'rgba(0,200,83,0.4)'
        }} initial={{
          opacity: 0
        }} animate={{
          opacity: shown(3) ? 1 : 0
        }} transition={{
          duration: 0.5,
          delay: 0.3,
          ease
        }} data-id="element-2747" data-mp="2mmz5ayr17647" />
            <motion.div className="absolute text-center" style={{
          left: PRODUCT_X[i],
          top: 452,
          width: PRODUCT_W
        }} initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: shown(3) ? 1 : 0,
          y: shown(3) ? 0 : 8
        }} transition={{
          duration: 0.7,
          delay: 0.35 + i * 0.14,
          ease
        }} data-id="element-2748" data-mp="1262ihkvm4y77">
              <p className="text-[17px] font-semibold tracking-[-0.015em] text-[#0B1712]" data-id="element-2749" data-mp="x2ju5up19nhk">
                {product.name}
              </p>
              <p className="mt-1.5 text-[12px] leading-snug text-[#7C8B82]" data-id="element-2750" data-mp="11wedgx0q80g4">
                {product.descriptor}
              </p>
            </motion.div>
            {/* converge toward the distribution outcome */}
            <motion.div className="absolute w-px" style={{
          left: PRODUCT_X[i] + PRODUCT_W / 2,
          top: 512,
          height: 28,
          backgroundColor: 'rgba(0,200,83,0.28)'
        }} initial={{
          opacity: 0
        }} animate={{
          opacity: shown(4) ? 1 : 0
        }} transition={{
          duration: 0.5,
          ease
        }} data-id="element-2751" data-mp="1fu2x61j251jl" />
          </div>)}
        <motion.div className="absolute h-px" style={{
        left: PRODUCT_X[0] + PRODUCT_W / 2,
        top: 540,
        width: PRODUCT_X[1] + PRODUCT_W / 2 - (PRODUCT_X[0] + PRODUCT_W / 2),
        backgroundColor: 'rgba(0,200,83,0.28)'
      }} initial={{
        scaleX: 0
      }} animate={{
        scaleX: shown(4) ? 1 : 0
      }} transition={{
        duration: 0.7,
        ease
      }} data-id="element-2752" data-mp="1sbdp8el2uxjl" />
        {connector(540, 24, 4)}

        <motion.div className="absolute text-center" style={{
        left: AXIS - 320,
        top: 576,
        width: 640
      }} initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: shown(4) ? 1 : 0,
        y: shown(4) ? 0 : 8
      }} transition={{
        duration: 0.8,
        delay: 0.2,
        ease
      }} data-id="element-2753" data-mp="14f9d93rzgh51">
          <p className="text-[24px] font-semibold tracking-[-0.02em] text-vexu-700" data-id="element-2754" data-mp="2tt4nkhck7xr9">
            Operator distribution
          </p>
          <p className="mt-3 text-[12px] text-[#8A998F]" data-id="element-2755" data-mp="2prj58gh9ar9o">
            {operatorAssets.join(' · ')}
          </p>
        </motion.div>

        {/* Regional opportunity */}
        <motion.div className="absolute flex items-start gap-12 border-t border-[#0B1712]/[0.09] pt-6" style={{
        left: 112,
        right: 112,
        top: 660
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: shown(5) ? 1 : 0
      }} transition={{
        duration: 0.9,
        ease
      }} data-id="element-2756" data-mp="32ayak232eymm">
          {regionalStages.map((item, i) => <React.Fragment key={item.name} data-id="element-2757" data-mp="3g4fjyu21euyx" data-mpi={i}>
              {i > 0 && <span className="mt-4 text-[12px] text-[#C3CCC5]" data-id="element-2758" data-mp="3l0mtewtn6olm">→</span>}
              <motion.div initial={{
            opacity: 0,
            y: 6
          }} animate={{
            opacity: shown(5) ? 1 : 0,
            y: shown(5) ? 0 : 6
          }} transition={{
            duration: 0.6,
            delay: i * 0.2,
            ease
          }} data-id="element-2759" data-mp="36bi8v8zdk8ac">
                <span className="text-[10.5px] font-semibold tracking-[0.22em] text-vexu-700/60 tabular-nums" data-id="element-2760" data-mp="2tq8b676ag0j">
                  {item.index}
                </span>
                <p className="mt-1.5 text-[17px] font-semibold tracking-[-0.01em] text-[#0B1712]" data-id="element-2761" data-mp="trvpezpch7nw">
                  {item.name}
                </p>
                <p className="mt-1 text-[12.5px] text-[#7C8B82]" data-id="element-2762" data-mp="jtg9lsvrpktp">
                  {item.detail}
                </p>
              </motion.div>
            </React.Fragment>)}
        </motion.div>

        {/* Takeaway */}
        <motion.div className="absolute" style={{
        left: 112,
        bottom: 48,
        width: 900
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: shown(5) ? 1 : 0
      }} transition={{
        duration: 0.9,
        delay: 0.4,
        ease
      }} data-id="element-2763" data-mp="a2bvevdx23zx">
          <p className="text-[21px] font-semibold tracking-[-0.015em] text-[#0B1712]" data-id="element-2764" data-mp="61t1mvsgqnv3">
            One integration can unlock an entirely new distribution channel.
          </p>
          <p className="mt-2 text-[13px] text-[#7C8B82]" data-id="element-2765" data-mp="3nbzs43on9cbb">
            Direct acquisition builds the brand. Telecom partnerships can
            accelerate regional scale.
          </p>
        </motion.div>
      </div>
    </SlideStage>;
}